import type { ArticleBlock, ArticleBlockCreate, Block } from '@/types/blocks'

/**
 * Сервер отдаёт у блока статьи ВЛОЖЕННЫЙ объект `article` и не отдаёт `articleId`
 * верхнего уровня, хотя тип его обещает. Из-за этого пикер не подставлял текущую
 * статью, а реордер и «Копия» падали `422` (`loc: ["body","article","articleId"]`).
 * Чиним на границе api — там же, где живёт нормализация кнопок.
 */
export const normalizeArticleBlock = (block: Block): Block => {
  if (block.type !== 'article') return block
  const nested = block.article
  if (block.articleId || !nested) return block
  return { ...block, articleId: nested.id }
}

/**
 * Тело записи блока статьи. Вложенный `article` и серверные `available`/`completed`/
 * `isCorrect` в `ArticleBlockCreate` не входят и вызывают `422` — выкидываем их
 * поимённо, а не «всё кроме известного»: остальные поля блока (`bgColor`,
 * `patternType`, `buttons`) обязаны доехать, `PUT` блока их иначе обнулит.
 *
 * `order` снимается тоже: путь «Копия» (`toBlockCreate` без явного второго
 * аргумента) обязан уйти без него, чтобы `createBlock` вычислил следующую
 * позицию сам (`computeNextOrder`) — то же поведение, что у блоков остальных
 * типов. Вызывающий, которому `order` нужен явно (реордер, сохранение из
 * панели), обязан прислать его вторым аргументом — так делает `toBlockCreate`.
 *
 * На вход приходит вариант `Block`, то есть `ArticleBlock & BlockMeta` — серверные
 * `id`/`createdAt`/`updatedAt` снимаем здесь же, чтобы вызывающему не пришлось
 * повторять это второй раз.
 */
type ArticleBlockFromServer = ArticleBlock &
  Partial<{
    id: number
    createdAt: string
    updatedAt: string
    originId: number | null
    completed: boolean
    isCorrect: boolean | null
  }>

export const toArticleCreate = (
  block: ArticleBlockFromServer,
): Omit<ArticleBlockCreate, 'order'> => {
  const {
    article: _article,
    available: _available,
    completed: _completed,
    isCorrect: _isCorrect,
    id: _id,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    order: _order,
    originId: _originId,
    ...body
  } = block
  return body as Omit<ArticleBlockCreate, 'order'>
}
